import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    nextTick,
    ref,
} from 'vue';
import { useI18n } from 'vue-i18n-bridge';
import type {
    ProofreadingIssue,
    ProofreadingIssueCategory,
} from '@/types/proofreading';
import { flattenIssueCategories } from '@/lib/utils/proofreadingTransform';
import {
    scrollIntoView,
    SCROLL_INTO_VIEW_DURATION_MS,
} from '@/lib/utils/proofreadingEditorUtil';
import IssueItem from './IssueItem.vue';
import IssueListGroup from './IssueListGroup.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'IssueList',
  props: {
    issueCategories: { type: Array, required: true },
    manuscriptKeys: { type: Array, required: true }
  },
  emits: ["issue-click", "issue-apply", "issue-revert", "issue-reject-toggle", "issue-hover"],
  setup(__props: any, { expose, emit }: { emit: ({
    (event: 'issue-click', payload: { issueId: string; episodeKey: string }): void;
    (event: 'issue-apply', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-revert', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-reject-toggle', payload: { categoryId: string; issue: ProofreadingIssue }): void;
    (event: 'issue-hover', issue: ProofreadingIssue, isHovered: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    issueCategories: ProofreadingIssueCategory[];
    manuscriptKeys: string[];
};



const { t } = useI18n();

const isGrouped = ref(true);
const issueListRef = ref<HTMLDivElement | null>(null);
const activeIssueIds = ref<string[]>([]);
const targetIssueId = ref<string | null>(null);

const ACCORDION_TRANSITION_MS = 200;

const flatIssues = computed(() =>
    flattenIssueCategories(props.issueCategories, props.manuscriptKeys),
);

const hasIssues = computed(() => flatIssues.value.length > 0);



function isIssueActive(issue: ProofreadingIssue): boolean {
    if (targetIssueId.value == null) return true;

    return activeIssueIds.value.some(
        (activeId) =>
            issue.id === activeId || (issue.locations?.some((loc) => loc.id === activeId) ?? false),
    );
}

function scrollToIssueId(issueId: string) {
    nextTick(() => {
        const item = issueListRef.value?.querySelector(`[data-issue-id="${issueId}"]`);

        setTimeout(() => {
            // アコーディオンの開閉アニメーションが終わったら、アイテムをスクロールして中央に表示
            if (item instanceof HTMLElement) {
                scrollIntoView(item, SCROLL_INTO_VIEW_DURATION_MS);
            }
        }, ACCORDION_TRANSITION_MS);
    });
}


function setActiveIssueIds(
    issueIds: string[] | null,
    options: { scroll?: boolean } = {},
) {
    const shouldScroll = options.scroll !== false;

    if (issueIds == null || issueIds.length === 0) {
        activeIssueIds.value = [];
        targetIssueId.value = null;
        return;
    }

    let scrollTargetId: string;

    function areSameIssueIds(a: string[], b: string[]): boolean {
        if (a.length !== b.length) return false;
        return a.every((id, index) => id === b[index]);
    }
    if (areSameIssueIds(issueIds, activeIssueIds.value) && targetIssueId.value != null) {
        const currentIndex = issueIds.indexOf(targetIssueId.value);
        const nextIndex =
            currentIndex < 0 || currentIndex >= issueIds.length - 1 ? 0 : currentIndex + 1;
        scrollTargetId = issueIds[nextIndex];
    } else {
        activeIssueIds.value = issueIds;
        scrollTargetId = issueIds[0]!;
    }

    targetIssueId.value = scrollTargetId;
    if (shouldScroll) {
        scrollToIssueId(scrollTargetId);
    }
}

expose({
    setActiveIssueIds,
});

function getCategoryLabel(categoryId: string): string {
    return props.issueCategories.find((category) => category.id === categoryId)?.label ?? '';
}

function onIssueClick(payload: { issueId: string; episodeKey: string }) {
    emit('issue-click', payload);
}

function onIssueApply(payload: { categoryId: string; issue: ProofreadingIssue }) {
    emit('issue-apply', payload);
}

function onIssueRevert(payload: { categoryId: string; issue: ProofreadingIssue }) {
    emit('issue-revert', payload);
}

function onIssueRejectToggle(payload: { categoryId: string; issue: ProofreadingIssue }) {
    emit('issue-reject-toggle', payload);
}

function onIssueHover(issue: ProofreadingIssue, isHovered: boolean) {
    emit('issue-hover', issue, isHovered);
}

return { __sfc: true,props, t, isGrouped, issueListRef, activeIssueIds, targetIssueId, ACCORDION_TRANSITION_MS, flatIssues, hasIssues, emit, isIssueActive, scrollToIssueId, setActiveIssueIds, getCategoryLabel, onIssueClick, onIssueApply, onIssueRevert, onIssueRejectToggle, onIssueHover, IssueItem, IssueListGroup }
}

})